import toast from "react-hot-toast";
import AuditsConsolidatedPillarSelection from "./Audits/AuditsConsolidatedPillarSelection";
import AuditsConsolidatedReportGenerateAction from "./Audits/AuditsConsolidatedReportGenerateAction";
import { initializePillarSelection } from "./Audits/consolidatedPillarSelection";
import LoadingButton from "#components/LoadingButton/index.js";
import MDButton from "#components/MDButton/index.js";
import Dialog from "#components/NoScrollDialog/index.js";
import Stepper from "#components/Stepper/index.js";
import { Close } from "@mui/icons-material";
import {
  Box,
  Card,
  CardActionArea,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLanguage } from "contexts/language.context";
import { useEffect, useRef, useState } from "react";
import TasksConsolidatedReportGeneration from "./Tasks/TasksConsolidatedReportGeneration";
import TasksConsolidatedReportSettings from "./Tasks/TasksConsolidatedReportSettings";
import branchesService from "services/branches-service";
import AuditsConsolidatedReportSettings from "./Audits/AuditsConsolidatedReportSettings";
import AuditsConsolidatedReportGeneration from "./Audits/AuditsConsolidatedReportGeneration";

export default function BranchReportsButtonDialog({
  children,
  buttonProps,
  branchGroup,
}) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({
    branchGroup,
    includeTaskPhotos: false,
  });
  const [loading, setLoading] = useState({});
  const session = useRef(0);
  const { label } = useLanguage();
  const [activeStep, setActiveStep] = useState(0);
  const isAudits = data.type === "audits-consolidated";
  const pillarSelectionStep = 2;
  const previewStep = isAudits ? 3 : 2;
  const steps = [
    {
      label: "Choose report",
      description: "Select the required report",
      level: 1,
    },
    {
      label: "Report settings",
      description: "Specify report parameters and settings",
      level: 2,
    },
    ...(isAudits
      ? [
          {
            label: label("select-pillars") || "Select pillars",
            description:
              label("select-pillars-description") ||
              "Choose pillars to include",
            level: 3,
          },
        ]
      : []),
    {
      label: "View and generate",
      description: "Preview and generate the report",
      level: previewStep + 1,
    },
  ];

  useEffect(() => {
    session.current += 1;
    if (open) {
      setActiveStep(0);
      setLoading({});
      setData({
        branchGroup,
        includeTaskPhotos: false,
      });
    }
    return () => {
      session.current += 1;
    };
  }, [open, branchGroup.id]);

  useEffect(() => {
    setData((current) => ({ ...current, branchGroup }));
  }, [branchGroup]);

  async function downloadPDF(url, filename, requestSession) {
    const response = await fetch(url);
    if (!response.ok)
      throw new Error(
        label("unable-to-generate-pdf") || "Unable to download report",
      );
    const blob = await response.blob();
    if (session.current !== requestSession) return;
    const link = document.createElement("a");
    const objectUrl = window.URL.createObjectURL(blob);
    link.href = objectUrl;
    link.download = filename;
    link.click();
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
  }

  async function generateReport(event) {
    event?.preventDefault();
    if (
      loading.actions ||
      !data.type ||
      (isAudits && activeStep > 0 && activeStep !== previewStep)
    )
      return;
    if (
      isAudits &&
      activeStep >= pillarSelectionStep &&
      !Array.isArray(data.selectedPillarIds)
    )
      return;
    if (activeStep < 2) {
      if (
        activeStep === 1 &&
        (!data.filters?.branches?.length || !data.filters?.statuses?.length)
      )
        return;
      setActiveStep(activeStep + 1);
      return;
    }

    const requestSession = session.current;
    setLoading({ ...loading, actions: true });

    try {
      if (data.type == "tasks-consolidated") {
        const includeTaskPhotos = Boolean(data.includeTaskPhotos);
        const [response, returnedData] =
          await branchesService.getConsolidatedTasksReportLink(
            { branchGroupId: data.branchGroup.id },
            {
              includeTaskPhotos,
              filters: {
                ...data.filters,
                date: {
                  ...data.filters.date,
                  from: data.filters.date?.from?.$d,
                  to: data.filters.date?.to?.$d,
                },
              },
            },
          );
        if (session.current !== requestSession) return;
        if (!response.ok)
          throw new Error(
            returnedData?.error?.message || "Unable to generate report",
          );
        if (response.ok) {
          await downloadPDF(
            returnedData.url,
            `${data.branchGroup.name}-CTR-ifpc360.pdf`,
            requestSession,
          );
        }
      } else if (data.type == "audits-consolidated") {
        const [response, returnedData] =
          await branchesService.getConsolidatedReportLink(
            { branchGroupId: data.branchGroup.id },
            {
              type: data.auditsReport,
              selected: data.selected,
              selectedPillarIds: data.selectedPillarIds,
            },
          );
        if (session.current !== requestSession) return;
        if (!response.ok)
          throw new Error(
            returnedData?.error?.message || "Unable to generate report",
          );
        if (response.ok) {
          await downloadPDF(
            returnedData.url,
            `${data.branchGroup.name}-CR-ifpc360.pdf`,
            requestSession,
          );
        }
      }
    } catch (error) {
      if (session.current === requestSession)
        toast.error(
          error.message ||
            label("unable-to-generate-pdf") ||
            "Unable to generate report",
        );
    } finally {
      if (session.current === requestSession)
        setLoading((current) => ({ ...current, actions: false }));
    }
  }

  return (
    <>
      {open ? (
        <Dialog
          fullWidth
          maxWidth="lg"
          open={open}
          onClose={() => {
            if (!loading.actions) setOpen(false);
          }}
          PaperProps={{
            component: "form",
            sx: {
              p: 1.5,
              borderRadius: "32px",
              height: "calc(100dvh - 64px)",
              overflow: "hidden",
            },
            onSubmit: (event) => {
              if (isAudits && activeStep === pillarSelectionStep) {
                event.preventDefault();
                return;
              }
              generateReport(event);
            },
          }}
        >
          <DialogTitle
            sx={{
              mt: -1,
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "start",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="inherit">
                "{data.branchGroup.name}" Reports
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setOpen(false);
                }}
                disabled={loading.actions}
              >
                <Close />
              </IconButton>
            </Box>

            <Stepper steps={steps} activeStep={activeStep} />
          </DialogTitle>
          <DialogContent sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {activeStep == 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 3,
                }}
              >
                <CheckedCard
                  checked={data.type == "audits-consolidated"}
                  onChange={(checked) => {
                    if (checked)
                      setData({
                        ...data,
                        type: "audits-consolidated",
                        selectedPillarIds: undefined,
                        pillarSelectionKey: undefined,
                      });
                  }}
                  icon="query_stats"
                  title={"Consolidated audits report"}
                  description={
                    "Generate a consolidated report containing audits for the selected branch group, and all of its branches."
                  }
                />
                <CheckedCard
                  checked={data.type == "tasks-consolidated"}
                  onChange={(checked) => {
                    if (checked)
                      setData({
                        ...data,
                        type: "tasks-consolidated",
                        selectedPillarIds: undefined,
                        pillarSelectionKey: undefined,
                      });
                  }}
                  icon="assignment"
                  title={"Consolidated tasks report"}
                  description={
                    "Generate a consolidated report containing tasks for the selected branch group, and all of its branches."
                  }
                />
              </Box>
            ) : null}

            {activeStep == 1 && data.type == "tasks-consolidated" ? (
              <TasksConsolidatedReportSettings data={data} setData={setData} />
            ) : null}

            {activeStep == 1 && data.type == "audits-consolidated" ? (
              <AuditsConsolidatedReportSettings data={data} setData={setData} />
            ) : null}

            {activeStep == 2 && data.type == "tasks-consolidated" ? (
              <TasksConsolidatedReportGeneration
                data={data}
                setData={setData}
              />
            ) : null}

            {activeStep === pillarSelectionStep && isAudits ? (
              <AuditsConsolidatedPillarSelection
                data={data}
                setData={setData}
                disabled={loading.actions}
              />
            ) : null}

            {activeStep === previewStep && isAudits ? (
              <AuditsConsolidatedReportGeneration
                data={data}
                setData={setData}
              />
            ) : null}
          </DialogContent>
          <DialogActions>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                gap: 1,
                justifyContent: "end",
              }}
            >
              <MDButton
                disabled={loading.actions}
                onClick={() => {
                  setOpen(false);
                }}
              >
                {label("cancel-button")}
              </MDButton>
              {activeStep !== 0 && (
                <MDButton
                  disabled={loading.actions}
                  onClick={() => {
                    if (activeStep !== 0) {
                      setActiveStep(activeStep - 1);
                    }
                  }}
                >
                  {label("back-button")}
                </MDButton>
              )}
              <Tooltip
                title={
                  data.type == "tasks-consolidated" &&
                  activeStep == 1 &&
                  (!data.filters?.branches?.length ||
                    !data.filters?.statuses?.length)
                    ? "Select at least one branch and one status from the above"
                    : activeStep == 0 && !data.type
                      ? "Select at least one type from the above"
                      : null
                }
              >
                <Box>
                  {activeStep == 1 && data.type == "audits-consolidated" ? (
                    data.continueAction?.(() => {
                      setData((current) => initializePillarSelection(current));
                      setActiveStep(pillarSelectionStep);
                    })
                  ) : isAudits && activeStep === pillarSelectionStep ? (
                    <AuditsConsolidatedReportGenerateAction
                      data={data}
                      disabled={
                        loading.actions ||
                        !Array.isArray(data.selectedPillarIds)
                      }
                      onSelect={(auditsReport) => {
                        setData((current) => ({ ...current, auditsReport }));
                        setActiveStep(previewStep);
                      }}
                    />
                  ) : (
                    <LoadingButton
                      loading={loading.actions}
                      disabled={
                        (data.type == "tasks-consolidated" &&
                          activeStep == 1 &&
                          (!data.filters?.branches?.length ||
                            !data.filters?.statuses?.length)) ||
                        loading.actions ||
                        (activeStep == 0 && !data.type)
                      }
                      type="submit"
                      color="info"
                      variant="gradient"
                    >
                      {activeStep >= 2
                        ? label("generate-button")
                        : label("next-button")}
                    </LoadingButton>
                  )}
                </Box>
              </Tooltip>
            </Box>
          </DialogActions>
        </Dialog>
      ) : null}
      <MDButton
        {...buttonProps}
        onClick={() => {
          setOpen(true);
        }}
      >
        {children}
      </MDButton>
    </>
  );
}

function CheckedCard({
  checked,
  onChange,
  icon,
  title,
  description,
  disabled,
}) {
  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        minHeight: "150px",
        borderRadius: "16px",
        borderColor: checked ? "info.main" : "grey.300",
        backgroundColor: checked ? theme.palette.info.main + "33" : "white",
        opacity: disabled ? 0.5 : 1,
      })}
    >
      <CardActionArea
        disabled={disabled}
        onClick={() => {
          onChange(true);
        }}
        sx={(theme) => ({
          p: 2,
          px: 2.5,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          justifyContent: "start",
        })}
      >
        <Typography
          gutterBottom
          variant="h6"
          component="div"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Icon fontSize="small">{icon}</Icon>
          &nbsp;{title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontWeight: 400,
            fontSize: "15px",
            lineHeight: "21px",
          }}
        >
          {description}
        </Typography>
      </CardActionArea>
    </Card>
  );
}
